import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import defaultArt from '../assets/images/dj_kip_art.png';
import { useApi } from '../context/ApiProvider';
import { getImg } from '../utils/getImg';
import { getTrackTitleWithMixers } from '../utils/artistUtils';
import MobileCard from './MobileCard';
import TrackActionButtons from './TrackActionButtons';
import { useSnackbar } from 'notistack';

const TrackMobileCard = ({
  item,
  showGenreLink = true,
  extraSmall = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    art_url_xl,
    artist,
    artists,
    release_date,
    title,
    id,
    artist_meta,
    label_name,
    genre,
    mix_type,
    purchase_url,
    user_fav,
  } = item;
  const [isFav, setIsFav] = useState(user_fav?.is_fav);
  const [showLikes, setShowLikes] = useState(user_fav?.show_likes);
  const api = useApi();
  let navigate = useNavigate();

  const trackImg = getImg(art_url_xl) || defaultArt;
  const releaseDateFormatted = release_date
    ? new Date(release_date).toLocaleDateString('en-US')
    : '';
  const trackTitle = getTrackTitleWithMixers(title, artists, mix_type);

  // Truncate text for extra small version
  const truncateText = (text, maxLength = 25) => {
    if (!text) return text;
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  const displayTitle = extraSmall ? truncateText(artist, 15) : artist;
  const displaySubtitle = extraSmall
    ? truncateText(trackTitle, 18)
    : trackTitle;

  // Determine which date to show and format it
  const getDateChip = () => {
    if (user_fav?.first_purchased_date) {
      const date = new Date(user_fav.first_purchased_date);
      return `Bought ${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })}`;
    } else if (user_fav?.first_liked_date) {
      const date = new Date(user_fav.first_liked_date);
      return `Liked ${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })}`;
    }
    return null;
  };

  const dateChip = getDateChip();

  function goToTrack() {
    navigate(`/tracks/${id}`);
  }

  async function handleFavoriteClick() {
    const response = await api.put(`/favs/track/${item.id}`);
    if (response.ok) {
      setIsFav(response.body.is_fav);
      handleLikeNotification(response.body.is_fav, 'favorites');
    }
  }

  async function handleLikeClick() {
    const response = await api.put(`/favs/track/${item.id}/likes-toggle`);
    if (response.ok) {
      setShowLikes(response.body.show_likes);
      handleLikeNotification(response.body.show_likes, 'likes');
    }
  }

  function handleLikeNotification(showAdded, type) {
    if (showAdded) {
      enqueueSnackbar(`Track added to ${type}!`);
    } else {
      enqueueSnackbar(`Track removed from ${type}!`);
    }
  }

  function handleGenreClick(genre) {
    const encodedGenre = encodeURIComponent(genre.trim());
    navigate(`/tracks?genre=${encodedGenre}`);
  }

  return (
    <MobileCard
      handleFavoriteClick={handleFavoriteClick}
      handleLikeClick={handleLikeClick}
      isFav={isFav}
      likes={user_fav?.user_likes}
      showLikes={showLikes}
      date={releaseDateFormatted}
      goToItem={goToTrack}
      img={trackImg}
      title={displayTitle}
      subtitle={displaySubtitle}
      subtitleExtra={label_name}
      chips={
        extraSmall || !showGenreLink
          ? [dateChip].filter(Boolean)
          : [genre, dateChip].filter(Boolean)
      }
      onChipClick={extraSmall || !showGenreLink ? undefined : handleGenreClick}
      cardStyles={
        extraSmall
          ? {
              '& .MuiCardHeader-avatar': { display: 'none' },
              maxWidth: '200px',
              width: '200px',
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiCardHeader-title': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiCardHeader-subheader': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }
          : {}
      }
    >
      {extraSmall ? (
        <TrackActionButtons
          id={id}
          artistName={artist_meta?.name || artist}
          trackName={title}
          purchase_url={purchase_url}
          is_purchased={user_fav?.is_purchased ?? false}
          mix_type={mix_type}
          size="small"
        />
      ) : (
        <TrackActionButtons
          id={id}
          artistName={artist_meta?.name || artist}
          trackName={title}
          purchase_url={purchase_url}
          is_purchased={user_fav?.is_purchased ?? false}
          mix_type={mix_type}
        />
      )}
    </MobileCard>
  );
};

TrackMobileCard.propTypes = {
  item: PropTypes.object.isRequired,
  showGenreLink: PropTypes.bool,
  extraSmall: PropTypes.bool,
};

export default TrackMobileCard;
